import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

import {
  CasoOntCliente,
  ConsultaOntCliente,
  OntParaFormulario,
  ROTULO_CASO,
  consultarOntCliente,
  registrarOntCliente,
} from './ont-cliente.actions';
import { CandidatoOnt, useOntClienteCandidatos } from './useOntClienteCandidatos';

type Props = {
  ordenTrabajo: any;
  /** Lo controla el check "Usar ONT cliente" del formulario. */
  abierto: boolean;
  /**
   * Se cierra el diálogo. `huboSeleccion` es false cuando el operador canceló,
   * para que el formulario pueda desmarcar el check.
   */
  onCerrar: (huboSeleccion: boolean) => void;
  /** Se llama con la ONT ya lista para el combo "ONT a Utilizar". */
  onOntSeleccionada: (ont: OntParaFormulario) => void;
};

/** Color del chip por caso: verde si no hay que tocar nada, ámbar si sí. */
const COLOR_CASO: Record<CasoOntCliente, 'success' | 'warning' | 'info'> = {
  A: 'success',
  B: 'warning',
  C: 'info',
  D: 'warning',
};

/**
 * "ONT del cliente" para la activación de un TRASLADO.
 *
 * El combo "ONT a Utilizar" solo ofrece el stock que lleva el técnico, así que
 * en un traslado de cliente antiguo no aparece la ONT que ya está instalada en
 * el domicilio. Acá se la busca en tickets, se consulta en qué estado está esa
 * serie dentro del inventario de EVA y, si hace falta, se la registra antes de
 * dejarla seleccionada.
 */
export default function OntClienteTrasladoDialog({
  ordenTrabajo,
  abierto,
  onCerrar,
  onOntSeleccionada,
}: Props) {
  const [serieSel, setSerieSel] = useState<string | null>(null);
  const [consulta, setConsulta] = useState<ConsultaOntCliente | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [errorConsulta, setErrorConsulta] = useState<string | null>(null);
  const [aceptaReingreso, setAceptaReingreso] = useState(false);
  const [registrando, setRegistrando] = useState(false);

  const { candidatos, cedula, contratoSoftnet, isLoading, tieneDatosDeTickets } =
    useOntClienteCandidatos(ordenTrabajo, abierto);

  const idOrdenTrabajo: string = ordenTrabajo?.id_orden_trabajo || '';

  const limpiar = () => {
    setSerieSel(null);
    setConsulta(null);
    setErrorConsulta(null);
    setAceptaReingreso(false);
  };

  const cancelar = () => {
    limpiar();
    onCerrar(false);
  };

  const elegirCandidato = async (candidato: CandidatoOnt) => {
    setSerieSel(candidato.serie);
    setConsulta(null);
    setErrorConsulta(null);
    setAceptaReingreso(false);
    setConsultando(true);
    try {
      const res = await consultarOntCliente(idOrdenTrabajo, {
        serie: candidato.serie,
        modelo: candidato.modelo,
        contrato_softnet: contratoSoftnet,
      });
      setConsulta(res);
    } catch (error: any) {
      // El backend explica el motivo (producto inexistente, orden que no es
      // traslado...); se muestra tal cual porque es accionable.
      const mensaje =
        error?.response?.data?.message ||
        error?.message ||
        'No se pudo consultar el estado de la ONT.';
      setErrorConsulta(mensaje);
    } finally {
      setConsultando(false);
    }
  };

  const acciones = consulta?.acciones || [];

  const descripcionAcciones = useMemo<string[]>(() => {
    if (!consulta) return [];
    const textos: string[] = [];
    if (acciones.includes('marcar_reingreso')) {
      textos.push(
        `Marcar la serie como reingreso: la histórica pasa a llamarse "${
          consulta.serie_en_inventario || consulta.serie
        }_REINGRESO".`,
      );
    }
    if (acciones.includes('crear_ubicacion')) {
      textos.push(
        `Crear la ubicación "${consulta.ubicacion_cliente?.nombre}" en la bodega ${
          consulta.ubicacion_cliente?.bodega_nombre || 'de clientes'
        }.`,
      );
    }
    if (acciones.includes('ingreso_inventario')) {
      textos.push(
        `Ingresar la serie ${consulta.serie} del producto ${
          consulta.producto?.codigo
        } a nombre del cliente.`,
      );
    }
    return textos;
  }, [consulta, acciones]);

  const faltaConfirmar = !!consulta?.requiere_confirmacion && !aceptaReingreso;
  const puedeUsar = !!consulta?.ont && !faltaConfirmar;

  const usar = async () => {
    if (!consulta?.ont) return;
    if (acciones.length === 0) {
      // Casos A y D: la serie ya existe en inventario, no se escribe nada.
      onOntSeleccionada(consulta.ont);
      toast.success('ONT del cliente seleccionada.');
      limpiar();
      onCerrar(true);
      return;
    }
    setRegistrando(true);
    try {
      const ont = await registrarOntCliente(consulta);
      onOntSeleccionada(ont);
      toast.success('ONT registrada a nombre del cliente y seleccionada.');
      limpiar();
      onCerrar(true);
    } catch (error: any) {
      const mensaje =
        error?.response?.data?.message ||
        error?.message ||
        'No se pudo registrar la ONT en inventario.';
      toast.error(mensaje);
    } finally {
      setRegistrando(false);
    }
  };

  return (
    <Dialog open={abierto} onClose={cancelar} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        ONT instalada en el domicilio
        <Typography variant="body2" color="text.secondary">
          Equipos que el cliente {cedula ? `(${cedula})` : ''} tiene registrados
          en tickets.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!isLoading && !tieneDatosDeTickets && (
          <Alert severity="warning">
            No se pudo leer la información del cliente en tickets. Revise que el
            contrato esté vinculado y vuelva a intentar.
          </Alert>
        )}

        {!isLoading && tieneDatosDeTickets && candidatos.length === 0 && (
          <Alert severity="info">
            El cliente no tiene ninguna ONT registrada en tickets, así que no hay
            serie que reutilizar. Desmarque el check y elija una ONT del stock
            del técnico.
          </Alert>
        )}

        {!isLoading && candidatos.length > 0 && (
          <List dense disablePadding>
            {candidatos.map((c) => (
              <ListItemButton
                key={c.serie}
                selected={serieSel === c.serie}
                onClick={() => elegirCandidato(c)}
                divider
              >
                <ListItemText
                  primary={c.serie}
                  secondary={[c.modelo, c.observaciones]
                    .filter(Boolean)
                    .join(' · ')}
                  primaryTypographyProps={{ fontFamily: 'monospace' }}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {consultando && (
          <Box display="flex" alignItems="center" gap={1} pt={2}>
            <CircularProgress size={18} />
            <Typography variant="body2">Consultando el inventario...</Typography>
          </Box>
        )}

        {errorConsulta && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorConsulta}
          </Alert>
        )}

        {consulta && !consultando && (
          <Box pt={2}>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip
                size="small"
                color={COLOR_CASO[consulta.caso]}
                label={ROTULO_CASO[consulta.caso]}
              />
              {consulta.producto && (
                <Typography variant="body2" color="text.secondary">
                  {consulta.producto.codigo} · {consulta.producto.descripcion}
                </Typography>
              )}
            </Box>

            <Typography variant="body2" gutterBottom>
              {consulta.caso_detalle}
            </Typography>

            {consulta.ubicacion_actual && (
              <Typography variant="body2" color="text.secondary">
                Consta en: {consulta.ubicacion_actual.bodega_nombre} /{' '}
                {consulta.ubicacion_actual.nombre}
              </Typography>
            )}

            {descripcionAcciones.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Al continuar se hará:
                </Typography>
                {descripcionAcciones.map((t) => (
                  <Typography key={t} variant="body2" component="div">
                    • {t}
                  </Typography>
                ))}
              </Alert>
            )}

            {consulta.advertencias.map((a) => (
              <Alert key={a} severity="warning" sx={{ mt: 1 }}>
                {a}
              </Alert>
            ))}

            {consulta.requiere_confirmacion && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={aceptaReingreso}
                    onChange={(e) => setAceptaReingreso(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Entiendo que la serie histórica se renombrará para poder
                    volver a ingresarla.
                  </Typography>
                }
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={cancelar} disabled={registrando}>
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          onClick={usar}
          loading={registrando}
          disabled={!puedeUsar}
        >
          {acciones.length === 0 ? 'Usar esta ONT' : 'Registrar y usar'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
